import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import SearchBar from './common/SearchBar';

const Header: React.FC = () => {
    const { user, logout } = useAuth();
    const { totalItems } = useCart();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    // Close mobile menu on escape key and manage body scroll
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isMobileMenuOpen) {
                closeMobileMenu();
            }
        };

        // Prevent body scroll when menu is open
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
            document.body.style.overflow = 'unset'; // Clean up on unmount
        };
    }, [isMobileMenuOpen]);

    return (
        <>
            <header className="header-fancy">
                <div className="header-container">
                    <div className="header-content">
                        {/* Logo */}
                        <Link to="/" className="logo-container">
                            <div className="logo-wrapper">
                                <div className="logo-icon">🥘</div>
                                <div className="logo-text">
                                    <div className="logo-title">Eldaï</div>
                                    {window.innerWidth >= 480 && (
                                        <div className="logo-subtitle">Commandes en ligne</div>
                                    )}
                                </div>
                            </div>
                            <div className="logo-decoration"></div>
                        </Link>

                        {/* Navigation */}
                        <nav className="nav-desktop">
                            <div className="nav-item">
                                <Link to="/" className="nav-link">
                                    <span className="nav-icon">🏠</span>
                                    <span>Accueil</span>
                                    <div className="nav-underline"></div>
                                </Link>
                            </div>
                            <div className="nav-item">
                                <Link to="/catalog" className="nav-link">
                                    <span className="nav-icon">📋</span>
                                    <span>Catalogue</span>
                                    <div className="nav-underline"></div>
                                </Link>
                            </div>
                            {user && (
                                <div className="nav-item">
                                    <Link to="/orders" className="nav-link">
                                        <span className="nav-icon">📦</span>
                                        <span>Commandes</span>
                                        <div className="nav-underline"></div>
                                    </Link>
                                </div>
                            )}
                            {user && (
                                <div className="nav-item">
                                    <Link to="/cart" className="nav-link">
                                        <span className="nav-icon">🛒</span>
                                        <span>Panier</span>
                                        {totalItems > 0 && (
                                            <span className="cart-badge-nav">
                                                {totalItems}
                                            </span>
                                        )}
                                        <div className="nav-underline"></div>
                                    </Link>
                                </div>
                            )}
                            {user?.role === 'admin' && (
                                <div className="nav-item">
                                    <Link to="/admin" className="nav-link">
                                        <span className="nav-icon">⚙️</span>
                                        <span>Administration</span>
                                        <div className="nav-underline"></div>
                                    </Link>
                                </div>
                            )}
                        </nav>

                        {/* Global Search */}
                        <div className="header-search-container">
                            <SearchBar
                                className="header-search"
                                placeholder="Rechercher des produits..."
                            />
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            className="mobile-menu-button"
                            onClick={toggleMobileMenu}
                            aria-label="Menu"
                        >
                            <div className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </button>

                        {/* User Actions */}
                        <div className="user-actions">
                            {user ? (
                                <>
                                    <div className="user-greeting">
                                        <span className="greeting-icon">👋</span>
                                        <span className="greeting-text">Bonjour, {user.first_name}</span>
                                    </div>
                                    <button onClick={handleLogout} className="logout-button">
                                        <span className="logout-icon">↗</span>
                                        <span className="logout-text">Déconnexion</span>
                                    </button>
                                </>
                            ) : (
                                <div className="auth-buttons">
                                    <Link to="/login" className="login-button">
                                        <span className="auth-icon">👤</span>
                                        <span className="auth-text">Connexion</span>
                                    </Link>
                                    <Link to="/register" className="register-button">
                                        <span className="auth-icon">✨</span>
                                        <span className="auth-text">Inscription</span>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay - Outside header for full viewport coverage */}
            {isMobileMenuOpen && (
                <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
            )}

            {/* Mobile Slide Menu - Outside header for proper layering */}
            <div className={`mobile-slide-menu ${isMobileMenuOpen ? 'open' : ''}`}>
                <div className="mobile-menu-header">
                    <div className="mobile-menu-title">Menu</div>
                    <button
                        className="mobile-menu-close"
                        onClick={closeMobileMenu}
                        aria-label="Fermer le menu"
                    >
                        ✕
                    </button>
                </div>

                <nav className="mobile-menu-nav">
                    <Link to="/" className="mobile-menu-link" onClick={closeMobileMenu}>
                        <span className="mobile-menu-icon">🏠</span>
                        <span>Accueil</span>
                    </Link>
                    <Link to="/catalog" className="mobile-menu-link" onClick={closeMobileMenu}>
                        <span className="mobile-menu-icon">📋</span>
                        <span>Catalogue</span>
                    </Link>
                    {user && (
                        <Link to="/orders" className="mobile-menu-link" onClick={closeMobileMenu}>
                            <span className="mobile-menu-icon">📦</span>
                            <span>Mes Commandes</span>
                        </Link>
                    )}
                    {user && (
                        <Link to="/cart" className="mobile-menu-link" onClick={closeMobileMenu}>
                            <span className="mobile-menu-icon">🛒</span>
                            <span>Panier</span>
                            {totalItems > 0 && (
                                <span className="mobile-menu-badge">
                                    {totalItems}
                                </span>
                            )}
                        </Link>
                    )}
                    {user?.role === 'admin' && (
                        <Link to="/admin" className="mobile-menu-link" onClick={closeMobileMenu}>
                            <span className="mobile-menu-icon">⚙️</span>
                            <span>Administration</span>
                        </Link>
                    )}
                </nav>

                {user ? (
                    <div className="mobile-menu-footer">
                        <div className="mobile-user-info">
                            <span className="mobile-greeting-icon">👋</span>
                            <span>Bonjour, {user.first_name}</span>
                        </div>
                        <button onClick={() => { handleLogout(); closeMobileMenu(); }} className="mobile-logout-button">
                            <span className="mobile-logout-icon">↗</span>
                            <span>Déconnexion</span>
                        </button>
                    </div>
                ) : (
                    <div className="mobile-menu-footer">
                        <Link to="/login" className="mobile-auth-button" onClick={closeMobileMenu}>
                            <span className="mobile-auth-icon">👤</span>
                            <span>Connexion</span>
                        </Link>
                        <Link to="/register" className="mobile-auth-button register" onClick={closeMobileMenu}>
                            <span className="mobile-auth-icon">✨</span>
                            <span>Inscription</span>
                        </Link>
                    </div>
                )}
            </div>
        </>
    );
};

export default Header;
